﻿import { CONTRATO_NOVO_DIAS, multaRescisao } from './extras';
import { calcularSalario, overallDoValor } from './financas';
import { valorSemMercado } from './valor-mercado';
import { gerarJogador } from './gerador';
import { jogadoresExternos, type JogadorExterno } from './ligas-mundo';
import { Rng } from './rng';
import type { Clube, Jogador, Posicao } from './tipos';
import { POSICOES } from './tipos';

/**
 * Mercado de transferências. A estrutura segue a engenharia reversa do original (../../../conhecimento/04):
 * o preço pedido parte do valor do jogador e sobe quando o clube vendedor tem poucos jogadores na posição;
 * clubes grandes (reputação >= 4) não vendem para clubes pequenos (reputação < 3); elencos têm limite máximo.
 * As constantes de tolerância são próprias.
 */

export const ELENCO_MIN = 18;
export const ELENCO_MAX = 45; // elencos reais chegam a 38 na Série A 2026 e a 42 na Série B (Londrina)
export const GOLEIROS_MIN = 2;
/** Mínimo por posição que a IA tenta manter. */
export const MINIMO_POR_POSICAO: Record<Posicao, number> = { G: 2, Z: 3, L: 3, M: 4, A: 3 };

// Tabelas do preço pedido (posições na ordem do original: G, L, Z, M, A).
const LIMIAR_PROFUNDIDADE: Record<Posicao, number> = { G: 3, L: 4, Z: 4, M: 5, A: 4 };
const ACRESCIMO_ESCASSEZ: Record<Posicao, number> = { G: 1.5, L: 1.5, Z: 1.5, M: 1.5, A: 2.0 };
const ACRESCIMO_ABAIXO_LIMIAR: Record<Posicao, number> = { G: 1, L: 1, Z: 1, M: 1, A: 1.5 };
const ACRESCIMO_PROFUNDO_BOM: Record<Posicao, number> = { G: 0.5, L: 0.2, Z: 0.2, M: 0.2, A: 0.5 };
const DESCONTO_PROFUNDO_RUIM: Record<Posicao, number> = { G: 0.15, L: 0.2, Z: 0.2, M: 0.1, A: 0.1 };

export interface Transferencia {
  temporada: number;
  rodada: number;
  jogadorId: number;
  jogador: string;
  /** null = jogador livre (sem clube) */
  de: number | null;
  para: number;
  valor: number;
}

/** O que o mercado precisa do estado do jogo (EstadoJogo satisfaz esta interface). */
export interface ContextoMercado {
  clubes: Clube[];
  livres: Jogador[];
  transferencias: Transferencia[];
  temporada: number;
  rodada: number;
  proximoIdJogador: number;
  /** só para a compra de jogadores de ligas estrangeiras (EstadoJogo tem os dois) */
  rngEstado?: number;
  externosComprados?: number[];
  /** jogo numa liga estrangeira: os jogadores dela já estão nos clubes e não entram no mercado externo */
  ligaMundo?: string;
  /** jogo com o mundo inteiro: todas as ligas têm clubes de verdade, então não há jogadores "externos" para comprar */
  mundo?: boolean;
}

export type Proposta =
  | { tipo: 'aceita'; valor: number }
  | { tipo: 'contraproposta'; valor: number; motivo: string }
  | { tipo: 'recusada'; motivo: string };

export interface OfertaRecebida {
  clubeId: number;
  valor: number;
}

const arredondar = (n: number): number => Math.round(n / 1000) * 1000;

function clubeDoJogador(ctx: ContextoMercado, jogadorId: number): Clube | undefined {
  return ctx.clubes.find((c) => c.elenco.some((j) => j.id === jogadorId));
}

function clubePorId(ctx: ContextoMercado, id: number): Clube {
  const c = ctx.clubes.find((x) => x.id === id);
  if (!c) throw new Error(`clube ${id} não existe`);
  return c;
}

export function contarPosicao(clube: Clube, pos: Posicao): number {
  return clube.elenco.filter((j) => j.posicao === pos).length;
}

/** Clubes grandes não negociam com clubes pequenos. */
export function negociaCom(vendedor: Clube, comprador: Clube): boolean {
  return !(vendedor.reputacao >= 4 && comprador.reputacao < 3);
}

/** Preço que o clube vendedor pede ao comprador. */
export function precoPedido(jogador: Jogador, vendedor: Clube): number {
  const pos = jogador.posicao;
  const quantos = contarPosicao(vendedor, pos);
  let v = jogador.valor;
  if (quantos >= LIMIAR_PROFUNDIDADE[pos]) {
    v = jogador.overall >= 30 && jogador.idade <= 35 ? v * (1 + ACRESCIMO_PROFUNDO_BOM[pos]) : v * (1 - DESCONTO_PROFUNDO_RUIM[pos]);
  } else if (quantos <= 1) {
    v = v * (1 + ACRESCIMO_ESCASSEZ[pos]);
  } else {
    v = v * (1 + ACRESCIMO_ABAIXO_LIMIAR[pos]);
  }
  return arredondar(v);
}

/** Verifica as regras de uma venda de `vendedor` para `comprador` (null = ok). */
function impedimentoVenda(jogador: Jogador, vendedor: Clube, comprador: Clube, ignorarReputacao = false): string | null {
  if (vendedor.id === comprador.id) return 'o jogador já é do seu clube';
  if (comprador.elenco.length >= ELENCO_MAX) return `elenco do comprador cheio (máximo ${ELENCO_MAX})`;
  if (vendedor.elenco.length <= ELENCO_MIN) return `o vendedor ficaria com menos de ${ELENCO_MIN} jogadores`;
  if (jogador.posicao === 'G' && contarPosicao(vendedor, 'G') <= GOLEIROS_MIN) return 'o vendedor ficaria sem goleiros suficientes';
  if (!ignorarReputacao && !negociaCom(vendedor, comprador)) return `${vendedor.nome} não negocia com clubes de baixa reputação`;
  return null;
}

/** Avalia uma proposta sem alterar nada. */
export function avaliarProposta(ctx: ContextoMercado, compradorId: number, jogadorId: number, oferta: number): Proposta {
  const comprador = clubePorId(ctx, compradorId);
  const vendedor = clubeDoJogador(ctx, jogadorId);
  if (!vendedor) return { tipo: 'recusada', motivo: 'jogador não encontrado' };
  const jogador = vendedor.elenco.find((j) => j.id === jogadorId) as Jogador;
  const impedimento = impedimentoVenda(jogador, vendedor, comprador);
  if (impedimento) return { tipo: 'recusada', motivo: impedimento };
  const preco = precoPedido(jogador, vendedor);
  if (comprador.caixa < Math.min(oferta, preco)) return { tipo: 'recusada', motivo: 'caixa insuficiente' };
  if (oferta >= preco) return { tipo: 'aceita', valor: preco };
  if (oferta < preco * 0.6) return { tipo: 'recusada', motivo: 'oferta muito abaixo do pedido' };
  if (comprador.caixa < preco) {
    // a oferta cabe no caixa, mas o clube só vende pelo preço pedido (abaixo dele, só contraproposta): diz isso com os números
    const mi = (n: number): string => `R$ ${(Math.round(n / 10_000) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} milhões`;
    return { tipo: 'recusada', motivo: `o ${vendedor.nome} só vende pelo valor pedido (${mi(preco)}), e seu caixa tem ${mi(comprador.caixa)}. Ofertas abaixo do pedido não são aceitas` };
  }
  return { tipo: 'contraproposta', valor: preco, motivo: `${vendedor.nome} pede ${preco}` };
}

function registrar(ctx: ContextoMercado, j: Jogador, de: Clube | null, para: Clube, valor: number): void {
  ctx.transferencias.push({ temporada: ctx.temporada, rodada: ctx.rodada, jogadorId: j.id, jogador: j.nome, de: de ? de.id : null, para: para.id, valor });
}

function mover(ctx: ContextoMercado, jogador: Jogador, de: Clube | null, para: Clube, valor: number): void {
  if (de) de.elenco = de.elenco.filter((j) => j.id !== jogador.id);
  else ctx.livres = ctx.livres.filter((j) => j.id !== jogador.id);
  jogador.salario = calcularSalario(jogador.overall, jogador.idade, jogador.posicao, para.nivel);
  jogador.contratoDias = CONTRATO_NOVO_DIAS;
  para.elenco.push(jogador);
  if (valor !== 0) {
    para.caixa -= valor;
    para.balanco.transferencias -= valor;
    para.balanco.compras = (para.balanco.compras ?? 0) + valor;
    if (de) {
      de.caixa += valor;
      de.balanco.transferencias += valor;
      de.balanco.vendas = (de.balanco.vendas ?? 0) + valor;
    }
  }
  registrar(ctx, jogador, de, para, valor);
}

/** Executa uma compra já negociada (valida as regras de novo). Lança erro se algo estiver errado. */
export function executarTransferencia(ctx: ContextoMercado, compradorId: number, jogadorId: number, valor: number): void {
  const comprador = clubePorId(ctx, compradorId);
  const vendedor = clubeDoJogador(ctx, jogadorId);
  if (!vendedor) throw new Error('jogador não encontrado');
  const jogador = vendedor.elenco.find((j) => j.id === jogadorId) as Jogador;
  const impedimento = impedimentoVenda(jogador, vendedor, comprador);
  if (impedimento) throw new Error(impedimento);
  if (comprador.caixa < valor) throw new Error('caixa insuficiente');
  mover(ctx, jogador, vendedor, comprador, valor);
}

/** Proposta do jogador humano (ou de qualquer clube): avalia e, se aceita, executa. */
export function proporCompra(ctx: ContextoMercado, compradorId: number, jogadorId: number, oferta: number): Proposta {
  const r = avaliarProposta(ctx, compradorId, jogadorId, oferta);
  if (r.tipo === 'aceita') executarTransferencia(ctx, compradorId, jogadorId, r.valor);
  return r;
}

/** Aceita uma contraproposta feita por `avaliarProposta`. */
export function aceitarContraproposta(ctx: ContextoMercado, compradorId: number, jogadorId: number, valor: number): void {
  const vendedor = clubeDoJogador(ctx, jogadorId);
  if (!vendedor) throw new Error('jogador não encontrado');
  const jogador = vendedor.elenco.find((j) => j.id === jogadorId) as Jogador;
  if (valor < precoPedido(jogador, vendedor)) throw new Error('valor abaixo do pedido');
  executarTransferencia(ctx, compradorId, jogadorId, valor);
}

/** Ofertas que clubes da IA fariam por um jogador do clube `vendedorId` (até 3). */
export function procurarCompradores(ctx: ContextoMercado, vendedorId: number, jogadorId: number, rng: Rng): OfertaRecebida[] {
  const vendedor = clubePorId(ctx, vendedorId);
  const jogador = vendedor.elenco.find((j) => j.id === jogadorId);
  if (!jogador) return [];
  const ofertas: (OfertaRecebida & { necessidade: number })[] = [];
  for (const c of ctx.clubes) {
    if (c.id === vendedorId || c.humano) continue;
    if (impedimentoVenda(jogador, vendedor, c)) continue;
    const quantos = contarPosicao(c, jogador.posicao);
    const necessidade = quantos < LIMIAR_PROFUNDIDADE[jogador.posicao] ? 1 : 0;
    // quem precisa da posição paga até 15% acima do valor; os demais, de 85% a 105%
    const fator = necessidade ? rng.faixa(100, 115) / 100 : rng.faixa(85, 105) / 100;
    const valor = arredondar(jogador.valor * fator);
    if (c.caixa < valor) continue;
    ofertas.push({ clubeId: c.id, valor, necessidade });
  }
  ofertas.sort((a, b) => b.necessidade - a.necessidade || b.valor - a.valor);
  return ofertas.slice(0, 3).map(({ clubeId, valor }) => ({ clubeId, valor }));
}

/** Vende um jogador do clube `vendedorId` para `compradorId` pelo valor combinado. */
/** opcoes.leilao: venda em leilão entre técnicos humanos (mercado aberto: a regra de reputação não vale). */
export function venderPara(ctx: ContextoMercado, vendedorId: number, jogadorId: number, compradorId: number, valor: number, opcoes: { leilao?: boolean } = {}): void {
  const vendedor = clubePorId(ctx, vendedorId);
  const comprador = clubePorId(ctx, compradorId);
  const jogador = vendedor.elenco.find((j) => j.id === jogadorId);
  if (!jogador) throw new Error('o jogador não está no clube');
  const impedimento = impedimentoVenda(jogador, vendedor, comprador, opcoes.leilao === true);
  if (impedimento) throw new Error(impedimento);
  if (comprador.caixa < valor) throw new Error('o comprador não tem caixa suficiente');
  // jogador com contrato: o clube humano que vende paga a multa de rescisão (a.jc do original)
  const multa = vendedor.humano ? multaRescisao(jogador) : 0;
  mover(ctx, jogador, vendedor, comprador, valor);
  if (multa > 0) {
    vendedor.caixa -= multa;
    vendedor.balanco.multas = (vendedor.balanco.multas ?? 0) + multa;
  }
}

/** Contrata um jogador livre (sem custo de transferência; o clube assume o salário). */
export function contratarLivre(ctx: ContextoMercado, clubeId: number, jogadorId: number): void {
  const clube = clubePorId(ctx, clubeId);
  const jogador = ctx.livres.find((j) => j.id === jogadorId);
  if (!jogador) throw new Error('jogador livre não encontrado');
  if (clube.elenco.length >= ELENCO_MAX) throw new Error(`elenco cheio (máximo ${ELENCO_MAX})`);
  mover(ctx, jogador, null, clube, 0);
}

/** Ágio sobre o valor de mercado que o clube estrangeiro pede pelo jogador. */
const AGIO_EXTERNO = 1.3;

export function externoPorId(id: number): JogadorExterno | undefined {
  return id < 0 ? jogadoresExternos()[-id - 1] : undefined;
}

/** Preço pedido por um jogador de liga estrangeira (valor de mercado da planilha com ágio; sem valor na planilha, o da fórmula). */
export function precoExterno(x: JogadorExterno): number {
  const base = x.valorMi > 0 ? x.valorMi * 1_000_000 : valorSemMercado(x.nome);
  return Math.max(100_000, arredondar(base * AGIO_EXTERNO));
}

/** Jogador do jogo a partir de um jogador de liga estrangeira: nome, idade, lado, força e valor vêm da planilha; o resto é sorteado. */
export function jogadorDeExterno(x: JogadorExterno, id: number, nivelClube: number, rng: Rng): Jogador {
  const pos: Posicao = x.posicao ?? 'M';
  const j = gerarJogador(id, pos, false, nivelClube, rng);
  j.nome = x.nome;
  j.idade = x.idade > 0 ? x.idade : 26;
  j.valor = x.valorMi > 0 ? Math.round((x.valorMi * 1_000_000) / 1000) * 1000 : valorSemMercado(x.nome);
  j.overall = x.valorMi > 0 ? (x.forca ?? 30) : overallDoValor(j.valor, j.idade, pos);
  j.salario = calcularSalario(j.overall, j.idade, pos, nivelClube);
  if (x.lado) j.lado = x.lado;
  if (x.valorMi <= 0) j.dadosEstimados = 'valor';
  return j;
}

/** Compra um jogador de liga estrangeira (id negativo de jogadoresExternos): ele entra no elenco do clube e some da busca. Devolve o preço pago. */
export function comprarExterno(ctx: ContextoMercado, clubeId: number, externoId: number): number {
  const x = externoPorId(externoId);
  if (!x) throw new Error('jogador não encontrado');
  if (ctx.mundo) throw new Error('neste jogo todos os clubes são de verdade: negocie com o clube do jogador');
  if (ctx.ligaMundo && x.ligaId === ctx.ligaMundo) throw new Error('esse jogador já está em um clube da sua liga');
  const clube = clubePorId(ctx, clubeId);
  const comprados = (ctx.externosComprados ??= []);
  if (comprados.includes(externoId)) throw new Error('esse jogador já foi contratado por outro clube');
  if (clube.elenco.length >= ELENCO_MAX) throw new Error(`elenco cheio (máximo ${ELENCO_MAX})`);
  const preco = precoExterno(x);
  if (clube.caixa < preco) throw new Error('caixa insuficiente');
  const rng = new Rng(ctx.rngEstado ?? Math.abs(externoId) + 1);
  const j = jogadorDeExterno(x, ctx.proximoIdJogador++, clube.nivel, rng);
  if (ctx.rngEstado !== undefined) ctx.rngEstado = rng.estado;
  mover(ctx, j, null, clube, preco);
  comprados.push(externoId);
  return preco;
}

/** Mantém um estoque de jogadores livres (pesos por posição na ordem de POSICOES: G, L, Z, M, A). */
export function reporLivres(ctx: ContextoMercado, rng: Rng, alvo = 12): void {
  while (ctx.livres.length < alvo) {
    const pos = POSICOES[rng.ponderado([1, 2, 2, 3, 2])] as Posicao;
    const j = gerarJogador(ctx.proximoIdJogador++, pos, false, rng.faixa(7, 12), rng);
    j.idade = rng.faixa(18, 35);
    ctx.livres.push(j);
  }
}

/** Clubes da IA que ficaram abaixo do mínimo de uma posição contratam livres (ou jovens novos). */
function completarElencos(ctx: ContextoMercado, rng: Rng): void {
  for (const c of ctx.clubes) {
    if (c.humano) continue;
    for (const pos of POSICOES) {
      while (contarPosicao(c, pos) < MINIMO_POR_POSICAO[pos] && c.elenco.length < ELENCO_MAX) {
        const livre = ctx.livres.filter((j) => j.posicao === pos).sort((a, b) => b.overall - a.overall)[0];
        if (livre) {
          mover(ctx, livre, null, c, 0);
        } else {
          const novo = gerarJogador(ctx.proximoIdJogador++, pos, false, Math.max(7, c.nivel - 3), rng);
          novo.idade = rng.faixa(17, 30);
          c.elenco.push(novo);
          novo.salario = calcularSalario(novo.overall, novo.idade, novo.posicao, c.nivel);
          registrar(ctx, novo, null, c, 0);
        }
      }
    }
  }
}

/** Movimentação entre clubes da IA numa rodada: de 0 a 2 compras e reposição de elencos. */
export function mercadoIA(ctx: ContextoMercado, rng: Rng): Transferencia[] {
  if (ctx.transferencias.length > 100) ctx.transferencias.splice(0, ctx.transferencias.length - 100);
  const antes = ctx.transferencias.length;
  const tentativas = rng.inteiro(3);
  for (let i = 0; i < tentativas; i++) {
    const compradores = ctx.clubes.filter((c) => !c.humano && c.caixa > 3_000_000 && c.elenco.length < ELENCO_MAX);
    if (compradores.length === 0) break;
    const comprador = rng.escolher(compradores);
    const pos = rng.escolher(POSICOES);
    const doGrupo = comprador.elenco.filter((j) => j.posicao === pos).sort((a, b) => b.overall - a.overall);
    const referencia = doGrupo[Math.min(doGrupo.length, MINIMO_POR_POSICAO[pos]) - 1];
    const piso = referencia ? referencia.overall + 3 : 0;
    const candidatos: { j: Jogador; vendedor: Clube }[] = [];
    for (const v of ctx.clubes) {
      if (v.humano || v.id === comprador.id) continue;
      if (contarPosicao(v, pos) <= MINIMO_POR_POSICAO[pos]) continue;
      for (const j of v.elenco) {
        if (j.posicao !== pos || j.overall < piso || j.valor > comprador.caixa * 0.5) continue;
        if (impedimentoVenda(j, v, comprador)) continue;
        candidatos.push({ j, vendedor: v });
      }
    }
    if (candidatos.length === 0) continue;
    candidatos.sort((a, b) => b.j.overall - a.j.overall);
    const escolhido = rng.escolher(candidatos.slice(0, 3));
    mover(ctx, escolhido.j, escolhido.vendedor, comprador, escolhido.j.valor);
  }
  completarElencos(ctx, rng);
  return ctx.transferencias.slice(antes);
}
